/**
 * useLosiInferenceVoice
 *
 * Connects the browser mic to a Losi Inference (LiveKit) room and plays
 * remote agent audio. Product label is Losi Inference.
 */

'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  Room,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  TranscriptionSegment
} from 'livekit-client'
import { getLoadedLiveKit, loadLiveKit } from '@/lib/livekit/load-livekit'

import type {
  LosiInferenceInteractionMode,
  LosiInferenceSessionOptions,
  LosiInferenceSurface
} from '@/lib/losi-inference'
import { createLiveKitAdapter } from '@/registry/lib/create-livekit-adapter'

export type LosiInferenceAgentUiState =
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'asleep'

export type LosiInferenceSessionResponse = {
  provider: string
  label: string
  serverUrl: string
  participantToken: string
  roomName: string
  identity: string
  agentName: string
  conversationId?: string | null
  chatId?: string | null
  surface?: LosiInferenceSurface
  interactionMode?: LosiInferenceInteractionMode
  toolCount?: number
  options: LosiInferenceSessionOptions
}

export type LosiInferenceStartOptions = Partial<LosiInferenceSessionOptions> & {
  agentId?: string
  chatId?: string
  surface?: LosiInferenceSurface
  interactionMode?: LosiInferenceInteractionMode
  /** Platform / tray tool ids to seed into the realtime registry. */
  enabledToolIds?: string[]
  /**
   * Mid-call reconnect (e.g. mode switch). Worker must not fire a fresh
   * "Hey I'm Losi" greeting — that looked like a session restart.
   */
  skipGreeting?: boolean
}

export type LosiInferenceTranscriptMeta = {
  final: boolean
  id?: string
}

export type LosiRealtimeDataEvent = {
  type: string
  [key: string]: unknown
}

type UseLosiInferenceVoiceOptions = {
  onTranscript?: (
    text: string,
    role?: 'user' | 'assistant',
    meta?: LosiInferenceTranscriptMeta
  ) => void
  onAgentState?: (state: LosiInferenceAgentUiState) => void
  onRealtimeEvent?: (event: LosiRealtimeDataEvent) => void
  onError?: (error: Error) => void
  onConnected?: (session: LosiInferenceSessionResponse) => void
  onDisconnected?: () => void
}

function mapAgentState(raw: string | undefined): LosiInferenceAgentUiState {
  switch (raw) {
    case 'listening':
      return 'listening'
    case 'thinking':
      return 'thinking'
    case 'speaking':
      return 'speaking'
    case 'asleep':
      return 'asleep'
    case 'initializing':
    case 'idle':
    default:
      return 'idle'
  }
}

const LOSI_INFERENCE_AUDIO_ATTR = 'data-losi-inference-audio'

/** Desktop remounts / overlapping auto-starts used to mint several LiveKit rooms. */
let losiInferenceStartGeneration = 0
let losiInferenceStartTail: Promise<void> = Promise.resolve()
/** Chat id for the in-flight or live session — remounts reuse instead of re-minting. */
let losiInferenceBoundChatId: string | null = null

/**
 * Bump the start generation so any in-flight start() becomes stale.
 * Must NOT run inside cleanup() — start() calls cleanup() before connect
 * and would abort itself.
 */
function invalidateLosiInferenceStarts() {
  losiInferenceStartGeneration += 1
}

type CleanupOptions = {
  /**
   * start() calls cleanup before connect. Clearing isConnecting there made
   * the UI drop out of "Connecting…" and let remount auto-start mint a
   * second room (desktop-only remount storms).
   */
  keepConnecting?: boolean
}

function removeLosiInferenceAudioElements(keep?: HTMLAudioElement | null) {
  if (typeof document === 'undefined') return
  document
    .querySelectorAll(`audio[${LOSI_INFERENCE_AUDIO_ATTR}]`)
    .forEach(node => {
      if (keep && node === keep) return
      const el = node as HTMLAudioElement
      try {
        el.pause()
        el.srcObject = null
        el.remove()
      } catch {
        // ignore
      }
    })
}

export function useLosiInferenceVoice(options: UseLosiInferenceVoiceOptions = {}) {
  const [isConnecting, setIsConnecting] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [agentState, setAgentState] =
    useState<LosiInferenceAgentUiState>('idle')
  const [inputLevel, setInputLevel] = useState(0)
  /** Live 0..1 amplitude for Aura Field — updated every frame, no React render. */
  const levelRef = useRef(-1)
  const [error, setError] = useState<Error | null>(null)
  const inputLevelPublishAtRef = useRef(0)
  const [activeOptions, setActiveOptions] =
    useState<LosiInferenceSessionOptions | null>(null)

  const roomRef = useRef<Room | null>(null)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  /** Intent for remote TTS mute (text_only). Applied on every track attach. */
  const remoteAudioMutedIntentRef = useRef(false)
  const micAdapterRef = useRef(createLiveKitAdapter())
  const remoteAdapterRef = useRef(createLiveKitAdapter())
  const agentStateRef = useRef<LosiInferenceAgentUiState>('idle')
  const mixRafRef = useRef<number | null>(null)
  const onTranscriptRef = useRef(options.onTranscript)
  const onAgentStateRef = useRef(options.onAgentState)
  const onRealtimeEventRef = useRef(options.onRealtimeEvent)
  const onErrorRef = useRef(options.onError)
  const onConnectedRef = useRef(options.onConnected)
  const onDisconnectedRef = useRef(options.onDisconnected)

  agentStateRef.current = agentState

  // Mix mic (listening) + remote TTS (speaking) into one levelRef for the aura.
  useEffect(() => {
    const mix = () => {
      const speaking = agentStateRef.current === 'speaking'
      const mic = micAdapterRef.current.levelRef.current
      const remote = remoteAdapterRef.current.levelRef.current
      const live = speaking
        ? remote >= 0
          ? remote
          : mic
        : mic >= 0
          ? mic
          : remote
      levelRef.current = live
      const now = performance.now()
      if (now - inputLevelPublishAtRef.current > 80) {
        inputLevelPublishAtRef.current = now
        // Idle timer should watch the mic, not TTS.
        setInputLevel(mic >= 0 ? mic : 0)
      }
      mixRafRef.current = requestAnimationFrame(mix)
    }
    mixRafRef.current = requestAnimationFrame(mix)
    return () => {
      if (mixRafRef.current != null) cancelAnimationFrame(mixRafRef.current)
      mixRafRef.current = null
    }
  }, [])

  useEffect(() => {
    onTranscriptRef.current = options.onTranscript
  }, [options.onTranscript])

  useEffect(() => {
    onAgentStateRef.current = options.onAgentState
  }, [options.onAgentState])

  useEffect(() => {
    onRealtimeEventRef.current = options.onRealtimeEvent
  }, [options.onRealtimeEvent])

  useEffect(() => {
    onErrorRef.current = options.onError
  }, [options.onError])

  useEffect(() => {
    onConnectedRef.current = options.onConnected
  }, [options.onConnected])

  useEffect(() => {
    onDisconnectedRef.current = options.onDisconnected
  }, [options.onDisconnected])

  const stopLevelMeters = useCallback(() => {
    micAdapterRef.current.dispose()
    remoteAdapterRef.current.dispose()
    levelRef.current = -1
    setInputLevel(0)
  }, [])

  /** When true, RoomEvent.Disconnected must not notify the parent (intentional teardown). */
  const suppressDisconnectNotifyRef = useRef(false)

  const cleanup = useCallback(async (opts?: CleanupOptions) => {
    stopLevelMeters()
    const room = roomRef.current
    roomRef.current = null
    if (room) {
      suppressDisconnectNotifyRef.current = true
      try {
        await room.disconnect()
      } catch {
        // ignore
      } finally {
        suppressDisconnectNotifyRef.current = false
      }
    }
    if (audioElRef.current) {
      audioElRef.current.pause()
      audioElRef.current.srcObject = null
      audioElRef.current.remove()
      audioElRef.current = null
    }
    // Concurrent start() races used to leave orphan <audio> nodes playing
    // (multiple LiveKit replies). Sweep every inference audio element.
    removeLosiInferenceAudioElements()
    // Reset mute intent so the next Full duplex session cannot inherit
    // text_only silence (transcripts would keep typing with no TTS).
    remoteAudioMutedIntentRef.current = false
    setIsActive(false)
    if (!opts?.keepConnecting) {
      setIsConnecting(false)
    }
    setIsMuted(false)
    setAgentState('idle')
  }, [stopLevelMeters])

  // Keep a stable unmount teardown. Depending on `cleanup` re-ran invalidate+
  // disconnect whenever the callback identity changed — that aborted the first
  // LiveKit connect on desktop remounts (/chat → /chat/[id]).
  const cleanupRef = useRef(cleanup)
  cleanupRef.current = cleanup
  useEffect(() => {
    return () => {
      // Only true unmount — do not invalidate module generation here.
      // Remounting RealtimeComposerBar must not abort a start that the new
      // instance is about to continue for the same chatId.
      void cleanupRef.current()
    }
  }, [])

  const attachRemoteAudio = useCallback((track: RemoteTrack) => {
    // Only called from a room's TrackSubscribed handler, so livekit is loaded
    if (track.kind !== getLoadedLiveKit().Track.Kind.Audio) return
    let el = audioElRef.current
    if (!el || !el.isConnected) {
      removeLosiInferenceAudioElements()
      el = document.createElement('audio')
      el.autoplay = true
      el.style.display = 'none'
      el.setAttribute(LOSI_INFERENCE_AUDIO_ATTR, '1')
      document.body.appendChild(el)
      audioElRef.current = el
    }
    // Re-apply intent every attach — TrackSubscribed can race ahead of
    // setRemoteAudioMuted, and a leftover muted element from text_only
    // used to leave Full duplex silently typing with no speech.
    el.muted = remoteAudioMutedIntentRef.current
    track.attach(el)
    // Retry play — a swallowed autoplay rejection left long silent typing
    // until something else poked the element (desync vs transcript stream).
    if (!el.muted) {
      const tryPlay = (attempt: number) => {
        void el!
          .play()
          .catch(() => {
            if (attempt >= 3) return
            window.setTimeout(() => tryPlay(attempt + 1), 250 * (attempt + 1))
          })
      }
      tryPlay(0)
    }
    // Agent TTS → aura while speaking (mic adapter stays for listening).
    remoteAdapterRef.current.setTrack(track.mediaStreamTrack)
  }, [])

  const setMuted = useCallback(async (muted: boolean) => {
    const room = roomRef.current
    if (!room) {
      setIsMuted(muted)
      return
    }
    try {
      if (!muted) {
        // Re-acquire permission on unmute (iOS may have revoked the track).
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            },
            video: false
          })
          stream.getTracks().forEach(track => track.stop())
        } catch {
          // Fall through — setMicrophoneEnabled will surface the failure.
        }
      }
      await room.localParticipant.setMicrophoneEnabled(!muted, {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      })
      setIsMuted(muted)
      if (!muted) {
        const micPub = room.localParticipant.getTrackPublication(
          getLoadedLiveKit().Track.Source.Microphone
        )
        const mediaStreamTrack = micPub?.track?.mediaStreamTrack
        if (mediaStreamTrack) {
          micAdapterRef.current.setTrack(mediaStreamTrack)
        }
      } else {
        micAdapterRef.current.setTrack(null)
      }
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Couldn't change mute. Check microphone access and try again.")
      onErrorRef.current?.(error)
    }
  }, [])

  const toggleMute = useCallback(async () => {
    await setMuted(!isMuted)
  }, [isMuted, setMuted])

  const setRemoteAudioMuted = useCallback((muted: boolean) => {
    remoteAudioMutedIntentRef.current = muted
    if (audioElRef.current) {
      audioElRef.current.muted = muted
      if (!muted) {
        void audioElRef.current.play().catch(() => {
          // User gesture may be required; startCall / unmute click covers most cases.
        })
      }
    }
  }, [])

  const sendText = useCallback(async (text: string) => {
    const room = roomRef.current
    const trimmed = text.trim()
    if (!room || !trimmed) return
    // LiveKit Agents RoomIO listens on topic `lk.chat` (not the default '').
    await room.localParticipant.sendText(trimmed, { topic: 'lk.chat' })
  }, [])

  const start = useCallback(
    async (sessionOptions?: LosiInferenceStartOptions) => {
      setError(null)
      setIsConnecting(true)

      // Serialize starts across remounts. Overlapping auto-starts on desktop
      // minted multiple rooms + orphan <audio> nodes (stacked replies).
      const myGeneration = ++losiInferenceStartGeneration
      const previousTail = losiInferenceStartTail
      let releaseTail!: () => void
      losiInferenceStartTail = new Promise<void>(resolve => {
        releaseTail = resolve
      })
      await previousTail

      const isStale = () => myGeneration !== losiInferenceStartGeneration

      try {
        if (isStale()) {
          setIsConnecting(false)
          return undefined
        }

        // Tear down any prior room without clearing Connecting… (keepConnecting).
        await cleanup({ keepConnecting: true })
        removeLosiInferenceAudioElements()
        setIsConnecting(true)

        if (isStale()) {
          setIsConnecting(false)
          return undefined
        }

        const boundChat =
          typeof sessionOptions?.chatId === 'string' && sessionOptions.chatId.trim()
            ? sessionOptions.chatId.trim()
            : null
        if (boundChat) {
          losiInferenceBoundChatId = boundChat
        }

        const res = await fetch('/api/losi-inference/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(sessionOptions || {}),
            enabledToolIds: sessionOptions?.enabledToolIds || undefined,
            skipGreeting: sessionOptions?.skipGreeting === true
          })
        })
        const data = (await res.json()) as LosiInferenceSessionResponse & {
          error?: string
        }
        if (!res.ok) {
          const raw = typeof data.error === 'string' ? data.error : ''
          // Never surface internal secret/env names in the UI.
          if (/VOICE_SERVER_SECRET|server config/i.test(raw)) {
            throw new Error(
              "Realtime couldn't start. Check your connection or continue in text chat."
            )
          }
          throw new Error(raw || "Realtime couldn't start. Try again or continue in text chat.")
        }

        if (isStale()) {
          // A newer start (or stop) owns the session — do not connect this room.
          setIsConnecting(false)
          return undefined
        }

        // livekit-client loads on first voice start, not with the chat bundle
        const { Room, RoomEvent, Track } = await loadLiveKit()

        if (isStale()) {
          setIsConnecting(false)
          return undefined
        }

        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        })
        roomRef.current = room

        // cleanup() cleared mute intent — set it again before connect so
        // TrackSubscribed cannot attach unmuted under text_only (or leave a
        // prior text_only mute stuck when returning to Full duplex).
        setRemoteAudioMuted(
          data.interactionMode === 'text_only' ||
            sessionOptions?.interactionMode === 'text_only'
        )

        room.on(
          RoomEvent.TrackSubscribed,
          (
            track: RemoteTrack,
            _publication: RemoteTrackPublication,
            _participant: RemoteParticipant
          ) => {
            if (isStale() || roomRef.current !== room) return
            attachRemoteAudio(track)
          }
        )

        room.on(
          RoomEvent.TranscriptionReceived,
          (
            segments: TranscriptionSegment[],
            participant?: RemoteParticipant | null
          ) => {
            if (isStale() || roomRef.current !== room) return
            // Stream interim + final ASR segments. Waiting only for final
            // made transcripts appear after the full phrase (BUG2).
            const isLocal =
              !participant ||
              participant.identity === room.localParticipant.identity ||
              Boolean((participant as { isLocal?: boolean }).isLocal)

            for (const segment of segments) {
              const text =
                typeof segment.text === 'string' ? segment.text.trim() : ''
              if (!text) continue
              onTranscriptRef.current?.(text, isLocal ? 'user' : 'assistant', {
                final: Boolean(segment.final),
                id: segment.id
              })
            }
          }
        )

        room.on(
          RoomEvent.ParticipantAttributesChanged,
          (changed: Record<string, string>) => {
            if (isStale() || roomRef.current !== room) return
            const raw = changed['lk.agent.state']
            if (typeof raw !== 'string') return
            const next = mapAgentState(raw)
            setAgentState(next)
            onAgentStateRef.current?.(next)
          }
        )

        room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
          if (isStale() || roomRef.current !== room) return
          if (topic && topic !== 'losi.realtime') return
          try {
            const decoded = new TextDecoder().decode(payload)
            const event = JSON.parse(decoded) as LosiRealtimeDataEvent
            if (event && typeof event.type === 'string') {
              onRealtimeEventRef.current?.(event)
            }
          } catch {
            // ignore malformed packets
          }
        })

        room.on(RoomEvent.Disconnected, () => {
          if (roomRef.current !== room) return
          roomRef.current = null
          if (audioElRef.current) {
            try {
              audioElRef.current.pause()
              audioElRef.current.srcObject = null
              audioElRef.current.remove()
            } catch {
              // ignore
            }
            audioElRef.current = null
          }
          // Server/agent hangup still left orphan <audio> playing without this.
          removeLosiInferenceAudioElements()
          setIsActive(false)
          setIsConnecting(false)
          setAgentState('idle')
          stopLevelMeters()
          // Intentional teardown (stop / pre-start cleanup / stale abort) must
          // not flip the parent out of realtime — that caused first-start
          // disconnect on desktop remount races.
          if (suppressDisconnectNotifyRef.current) return
          losiInferenceBoundChatId = null
          onDisconnectedRef.current?.()
        })

        await room.connect(data.serverUrl, data.participantToken)

        if (isStale() || roomRef.current !== room) {
          suppressDisconnectNotifyRef.current = true
          try {
            await room.disconnect()
          } catch {
            // ignore
          } finally {
            suppressDisconnectNotifyRef.current = false
          }
          setIsConnecting(false)
          return undefined
        }

        const wantMic =
          data.interactionMode !== 'speak_only' &&
          sessionOptions?.interactionMode !== 'speak_only'

        if (wantMic) {
          // iOS Safari: LiveKit setMicrophoneEnabled alone often yields a silent
          // track. Capture once via getUserMedia in this user gesture, then
          // publish that same MediaStreamTrack so we don't discard the live mic.
          let preflight: MediaStream | null = null
          try {
            preflight = await navigator.mediaDevices.getUserMedia({
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
              },
              video: false
            })
          } catch (micErr) {
            const error =
              micErr instanceof Error
                ? micErr
                : new Error(
                    "Microphone access is blocked. Allow the mic in System Settings, then try again."
                  )
            onErrorRef.current?.(error)
            throw error
          }

          const mediaTrack = preflight.getAudioTracks()[0]
          if (!mediaTrack) {
            preflight.getTracks().forEach(track => track.stop())
            throw new Error(
              "No microphone was found. Plug one in or pick another input, then try again."
            )
          }

          try {
            await room.localParticipant.publishTrack(mediaTrack, {
              source: Track.Source.Microphone,
              name: 'microphone'
            })
          } catch {
            // Fallback if publishTrack rejects the raw track.
            await room.localParticipant.setMicrophoneEnabled(true, {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            })
            preflight.getTracks().forEach(track => track.stop())
            preflight = null
          }

          const micPub = room.localParticipant.getTrackPublication(
            Track.Source.Microphone
          )
          const publishedTrack = micPub?.track?.mediaStreamTrack || mediaTrack
          if (publishedTrack) {
            micAdapterRef.current.setTrack(publishedTrack)
          }
          setIsMuted(false)
        } else {
          await room.localParticipant.setMicrophoneEnabled(false)
          setIsMuted(true)
        }

        // Always apply — leaving text_only used to leave <audio muted> stuck.
        setRemoteAudioMuted(
          data.interactionMode === 'text_only' ||
            sessionOptions?.interactionMode === 'text_only'
        )

        room.remoteParticipants.forEach(participant => {
          const stateAttr = participant.attributes?.['lk.agent.state']
          if (stateAttr) {
            const next = mapAgentState(stateAttr)
            setAgentState(next)
            onAgentStateRef.current?.(next)
          }
          participant.audioTrackPublications.forEach(publication => {
            if (publication.track) {
              attachRemoteAudio(publication.track as RemoteTrack)
            }
          })
        })

        if (isStale() || roomRef.current !== room) {
          suppressDisconnectNotifyRef.current = true
          try {
            await room.disconnect()
          } catch {
            // ignore
          } finally {
            suppressDisconnectNotifyRef.current = false
          }
          setIsConnecting(false)
          return undefined
        }

        setActiveOptions(data.options)
        setIsActive(true)
        setIsConnecting(false)
        onConnectedRef.current?.(data)
        return data
      } catch (err) {
        if (isStale()) {
          setIsConnecting(false)
          return undefined
        }
        const error =
          err instanceof Error ? err : new Error('Losi Inference connection failed')
        setError(error)
        setIsConnecting(false)
        setIsActive(false)
        onErrorRef.current?.(error)
        await cleanup()
        throw error
      } finally {
        releaseTail()
      }
    },
    [attachRemoteAudio, cleanup, setRemoteAudioMuted, stopLevelMeters]
  )

  const stop = useCallback(async () => {
    // Invalidate first so an in-flight start cannot reconnect after hangup
    // (fetch/connect races used to mint a second LiveKit room post-stop).
    invalidateLosiInferenceStarts()
    losiInferenceBoundChatId = null
    await cleanup()
  }, [cleanup])

  return {
    isConnecting,
    isActive,
    isMuted,
    agentState,
    inputLevel,
    /** 0..1 live amplitude for LosiRealtimeAura / AuraField — no re-render. */
    levelRef,
    error,
    activeOptions,
    start,
    stop,
    setMuted,
    toggleMute,
    setRemoteAudioMuted,
    sendText
  }
}
